import { type VariantProps, cva } from 'class-variance-authority';
import invariant from 'tiny-invariant';
import { Icon } from '../../Icon/Icon';
import { TextLink } from '../../TextLink/TextLink';

const breadcrumbs = cva('flex items-center space-x-xxs');

export type Breadcrumb = {
  /**
   * the link target becomes the rendered `<a>` must be a fully resolved
   * URL (e.g. `useLocation().pathname` or `generatePath('/foo/:id', { id })`),
   * never a raw route template like `/foo/:id`. React Router matches the literal
   * `:id` segment, so unresolved templates render navigable but broken links and
   * pollute analytics that read IDs from URL params!
   */
  to: string;
  text: string;
  icon?: React.ReactNode;
};

type BreadcrumbsProps = VariantProps<typeof breadcrumbs> & {
  crumbs: Breadcrumb[];
  showIndicators?: boolean;
};

export const Breadcrumbs = ({ crumbs }: BreadcrumbsProps) => {
  invariant(
    crumbs.length <= 3,
    '[UI:Breadcrumbs] `crumbs` param must be less than or equal to 3, as per designs',
  );

  const [crumb1, crumb2, crumb3] = crumbs;

  return (
    <div className={breadcrumbs()}>
      <Breadcrumb {...crumb1} />
      {crumb2 && (
        <>
          <Icon name="chevron_right" size="large" />
          <Breadcrumb {...crumb2} />
        </>
      )}
      {crumb3 && (
        <>
          <Icon name="chevron_right" size="large" />
          <Breadcrumb last {...crumb3} />
        </>
      )}
    </div>
  );
};

const breadcrumb = cva('flex items-center space-x-xs');

type BreadcrumbProps = Breadcrumb & { last?: boolean; showIndicator?: boolean };

const Breadcrumb = ({ to, text, icon, last = false }: BreadcrumbProps) => (
  <div className={breadcrumb()}>
    {icon && <div className="h-[16px] w-[16px]">{icon}</div>}
    <TextLink to={to} variant={last ? 'breadcrumbDark' : 'breadcrumb'}>
      {text}
    </TextLink>
  </div>
);
